// Single source of truth for the portfolio copy. Facts come from the LinkedIn profile
// (linkedin.com/in/isaacleao) and the CV PDFs in public/cv.

export type Lang = 'en' | 'pt';
type Bilingual<T> = Record<Lang, T>;

export const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || '';
export const EMAIL = 'isaacribeiroleao@gmail.com';
export const GITHUB_USER = 'IsaacRibeiroLeao';
export const LINKEDIN_URL = 'https://www.linkedin.com/in/isaacleao/';

/** Booking link when configured, otherwise a pre-filled email. */
export const contactHref = (lang: Lang): string =>
  BOOKING_URL ||
  `mailto:${EMAIL}?subject=${encodeURIComponent(
    lang === 'pt' ? 'Vamos conversar sobre uma vaga' : "Let's talk about a role"
  )}`;

export const hero: Bilingual<{
  line1: string;
  line2: string;
  sub: string;
  primary: string;
  secondary: string;
}> = {
  en: {
    line1: 'Serving 1M+',
    line2: 'public users.',
    sub: 'Isaac Ribeiro Leão, Backend Engineer. I build the APIs and microservices behind public healthcare and citizen portals. Open to roles in Canada and remote.',
    primary: BOOKING_URL ? 'Book a conversation' : 'Email me',
    secondary: 'Download CV',
  },
  pt: {
    line1: 'Servindo 1M+',
    line2: 'de cidadãos.',
    sub: 'Isaac Ribeiro Leão, Backend Engineer. Construo as APIs e os microsserviços por trás de portais públicos de saúde e cidadania. Aberto a vagas no Canadá e remotas.',
    primary: BOOKING_URL ? 'Agendar conversa' : 'Enviar e-mail',
    secondary: 'Baixar CV',
  },
};

export const stats: Bilingual<{ title: string; items: { value: string; text: string }[] }> = {
  en: {
    title: 'Results in production',
    items: [
      { value: '−90%', text: 'post-release defects on the Citizen Portal, which serves 1M+ users (ETIPI)' },
      { value: '−40%', text: 'deployment time after standardizing Docker and CI/CD (ETIPI)' },
      { value: '−30%', text: 'API response time on Paciente Integrado, with Redis and SQL tuning (MedSafe)' },
    ],
  },
  pt: {
    title: 'Resultados em produção',
    items: [
      { value: '−90%', text: 'de defeitos pós-release no Portal do Cidadão, que atende 1M+ de usuários (ETIPI)' },
      { value: '−40%', text: 'no tempo de deploy após padronizar Docker e CI/CD (ETIPI)' },
      { value: '−30%', text: 'no tempo de resposta das APIs do Paciente Integrado, com Redis e ajuste de SQL (MedSafe)' },
    ],
  },
};

export const about: Bilingual<{
  paragraph: string;
  facts: { label: string; value: string }[];
}> = {
  en: {
    paragraph:
      'I write backends in TypeScript with NestJS, in Java with Spring Boot and in C# with .NET Core. I model domains with Clean Architecture and DDD, ship on AWS (SQS, Lambda, API Gateway, S3, Cognito) with Docker and CI/CD, and keep data in PostgreSQL, MySQL, DynamoDB and Redis. APIs are secured with OAuth2 and JWT. On the frontend I work with React, Angular and Next.js.',
    facts: [
      { label: 'Based in', value: 'São Paulo, Brazil' },
      { label: 'Citizenship', value: 'Canadian' },
      { label: 'Languages', value: 'Portuguese and English, native' },
      { label: 'Education', value: 'Software Engineering Technician, Conestoga College, 2022 to 2024' },
    ],
  },
  pt: {
    paragraph:
      'Escrevo backends em TypeScript com NestJS, em Java com Spring Boot e em C# com .NET Core. Modelo domínios com Clean Architecture e DDD, publico na AWS (SQS, Lambda, API Gateway, S3, Cognito) com Docker e CI/CD, e guardo dados em PostgreSQL, MySQL, DynamoDB e Redis. As APIs são protegidas com OAuth2 e JWT. No frontend trabalho com React, Angular e Next.js.',
    facts: [
      { label: 'Mora em', value: 'São Paulo, Brasil' },
      { label: 'Cidadania', value: 'Canadense' },
      { label: 'Idiomas', value: 'Português e inglês, nativos' },
      { label: 'Formação', value: 'Software Engineering Technician, Conestoga College, 2022 a 2024' },
    ],
  },
};

export const projectKinds: Bilingual<Record<string, string>> = {
  en: {
    'paciente-integrado': 'Public healthcare',
    'pi-digital': 'Citizen portal',
    ppi: 'Early childhood program',
    caseme: 'Event planner',
    gcpreach: 'AI study copilot',
    'gerenciador-github-ia': 'AI for pull requests',
    jarvis: 'Voice assistant',
  },
  pt: {
    'paciente-integrado': 'Saúde pública',
    'pi-digital': 'Portal do cidadão',
    ppi: 'Programa de primeira infância',
    caseme: 'Planejador de eventos',
    gcpreach: 'Copiloto de estudo com IA',
    'gerenciador-github-ia': 'IA para pull requests',
    jarvis: 'Assistente de voz',
  },
};

export const nav: Bilingual<{ id: string; label: string }[]> = {
  en: [
    { id: 'experience', label: 'Experience' },
    { id: 'projects', label: 'Projects' },
    { id: 'about', label: 'Stack' },
    { id: 'contact', label: 'Contact' },
  ],
  pt: [
    { id: 'experience', label: 'Experiência' },
    { id: 'projects', label: 'Projetos' },
    { id: 'about', label: 'Stack' },
    { id: 'contact', label: 'Contato' },
  ],
};

export interface Job {
  company: string;
  role: string;
  period: string;
  context: string;
  did: string[];
  stack: string[];
}

export const experience: Bilingual<{ title: string; intro: string; jobs: Job[] }> = {
  en: {
    title: 'Where the work happened',
    intro:
      'Public healthcare and citizen services, where an outage or a slow query reaches real people.',
    jobs: [
      {
        company: 'HeyDev',
        role: 'Full Stack Engineer (contract)',
        period: 'Jan 2026 to present',
        context: 'Custom software studio, remote.',
        did: [
          'Moved legacy bottlenecks to asynchronous messaging on AWS SQS.',
          'Containerized services with Docker and tightened CI/CD, with automated tests gating releases.',
          'Shorter release cycles and better sprint velocity across teams.',
        ],
        stack: ['NestJS', 'AWS SQS', 'Docker', 'CI/CD'],
      },
      {
        company: 'MedSafe Brasil',
        role: 'Tech Lead, then Full Stack Developer',
        period: 'Mar 2024 to Feb 2026',
        context:
          'Piauí Paciente Integrado: regional healthcare across federal, state and municipal networks.',
        did: [
          'Built real-time clinical traceability for 100% audit compliance.',
          'Shipped a multi-channel notification engine (app, SMS, email) and a tablet-based mobile EHR.',
          'Enforced Clean Architecture and DDD, optimized SQL and added Redis caching: API response time down 30%, technical debt down 25%.',
        ],
        stack: ['Java Spring Boot', 'Angular 18', 'Next.js', 'PostgreSQL', 'Redis'],
      },
      {
        company: 'ETIPI',
        role: 'System Analyst',
        period: 'Feb 2024 to Feb 2026',
        context: 'Citizen Portal: 400+ digital services for over 1 million active users.',
        did: [
          'Replaced tightly coupled legacy services with core microservices on .NET Core.',
          'Post-release defects down 90%.',
          'Standardized Docker and CI/CD pipelines: deployment time down 40%.',
        ],
        stack: ['.NET Core', 'REST', 'Docker', 'CI/CD', 'Clean Architecture'],
      },
    ],
  },
  pt: {
    title: 'Onde o trabalho aconteceu',
    intro:
      'Saúde pública e serviços ao cidadão, onde uma queda ou uma consulta lenta chega em gente de verdade.',
    jobs: [
      {
        company: 'HeyDev',
        role: 'Full Stack Engineer (contrato)',
        period: 'Jan 2026 até hoje',
        context: 'Software house sob medida, remoto.',
        did: [
          'Migrei gargalos legados para mensageria assíncrona com AWS SQS.',
          'Containerizei serviços com Docker e reforcei o CI/CD, com testes automatizados barrando releases.',
          'Ciclos de release menores e mais velocidade de sprint entre os times.',
        ],
        stack: ['NestJS', 'AWS SQS', 'Docker', 'CI/CD'],
      },
      {
        company: 'MedSafe Brasil',
        role: 'Tech Lead, antes Desenvolvedor Full Stack',
        period: 'Mar 2024 a Fev 2026',
        context:
          'Piauí Paciente Integrado: saúde regional nas redes federal, estadual e municipal.',
        did: [
          'Construí rastreabilidade clínica em tempo real, com 100% de conformidade em auditoria.',
          'Entreguei motor de notificações multicanal (app, SMS, e-mail) e prontuário móvel em tablet.',
          'Apliquei Clean Architecture e DDD, otimizei SQL e adicionei cache com Redis: tempo de resposta da API 30% menor, dívida técnica 25% menor.',
        ],
        stack: ['Java Spring Boot', 'Angular 18', 'Next.js', 'PostgreSQL', 'Redis'],
      },
      {
        company: 'ETIPI',
        role: 'Analista de Sistemas',
        period: 'Fev 2024 a Fev 2026',
        context: 'Portal do Cidadão: 400+ serviços digitais para mais de 1 milhão de usuários ativos.',
        did: [
          'Troquei serviços legados fortemente acoplados por microsserviços em .NET Core.',
          'Defeitos pós-release 90% menores.',
          'Padronizei Docker e pipelines de CI/CD: tempo de deploy 40% menor.',
        ],
        stack: ['.NET Core', 'REST', 'Docker', 'CI/CD', 'Clean Architecture'],
      },
    ],
  },
};

export interface Project {
  slug: string;
  name: string;
  summary: string;
  stack: string[];
  repo?: string; // "owner/name", enables live GitHub stats
  liveUrl?: string;
}

export const projects: Bilingual<{ title: string; intro: string; items: Project[]; code: string; live: string }> = {
  en: {
    title: 'Selected projects',
    intro: 'Production government platforms first, then what I build on my own time.',
    code: 'Code',
    live: 'Live',
    items: [
      {
        slug: 'paciente-integrado',
        name: 'Paciente Integrado',
        summary:
          'Statewide patient tracking for Piauí. Triage, consultations and notifications across federal, state and municipal networks. I led the technical side.',
        stack: ['Java Spring Boot', 'Angular', 'PostgreSQL', 'Redis'],
        liveUrl: 'https://paciente-integrado.pi.gov.br/login',
      },
      {
        slug: 'pi-digital',
        name: 'PI Digital (Gov.pi)',
        summary:
          'The state citizen portal: 400+ services in one place, from documents and appointments to Detran and municipal access.',
        stack: ['.NET Core', 'Microservices', 'Docker'],
        liveUrl: 'https://pidigital.pi.gov.br/',
      },
      {
        slug: 'ppi',
        name: 'Piauí Primeira Infância',
        summary:
          'Early childhood program platform that tracks services and resources for children across administrative regions.',
        stack: ['TypeScript', 'PostgreSQL', 'REST'],
        liveUrl: 'https://ppi.pi.gov.br/login',
      },
      {
        slug: 'caseme',
        name: 'CaseMe',
        summary:
          'Event and wedding planner with live budget tracking, vendor workflows and RSVPs. Multi-tenant isolation through Supabase Row-Level Security, DOMPurify against XSS, Vitest suites.',
        stack: ['React 19', 'TypeScript', 'Supabase', 'Vitest'],
      },
      {
        slug: 'gcpreach',
        name: 'GCPreach',
        summary:
          'Study copilot that turns handwritten notes (OCR), drafts and YouTube transcripts into structured outlines with Gemini. Falls back from Gemini 2.5 Flash to 2.0 Flash on HTTP 503 and enforces JSON-schema output.',
        stack: ['Gemini API', 'React 19', 'Supabase', 'shadcn/ui'],
      },
      {
        slug: 'gerenciador-github-ia',
        name: 'GitHub AI Automation',
        summary:
          'Reads commit history through the GitHub REST API and uses an LLM to write pull-request summaries and plain-language progress updates for stakeholders.',
        stack: ['Python', 'GitHub API', 'LLM'],
      },
      {
        slug: 'jarvis',
        name: 'JarvisAI',
        summary: 'Voice assistant in Python with speech recognition and OpenAI.',
        stack: ['Python', 'OpenAI', 'PyAudio'],
      },
    ],
  },
  pt: {
    title: 'Projetos selecionados',
    intro: 'Primeiro as plataformas de governo em produção, depois o que construo no meu tempo.',
    code: 'Código',
    live: 'Ao vivo',
    items: [
      {
        slug: 'paciente-integrado',
        name: 'Paciente Integrado',
        summary:
          'Acompanhamento de pacientes em todo o Piauí. Triagem, consultas e notificações entre as redes federal, estadual e municipal. Liderei a parte técnica.',
        stack: ['Java Spring Boot', 'Angular', 'PostgreSQL', 'Redis'],
        liveUrl: 'https://paciente-integrado.pi.gov.br/login',
      },
      {
        slug: 'pi-digital',
        name: 'PI Digital (Gov.pi)',
        summary:
          'O portal do cidadão do estado: 400+ serviços num só lugar, de documentos e agendamentos ao Detran e acesso municipal.',
        stack: ['.NET Core', 'Microsserviços', 'Docker'],
        liveUrl: 'https://pidigital.pi.gov.br/',
      },
      {
        slug: 'ppi',
        name: 'Piauí Primeira Infância',
        summary:
          'Plataforma do programa de primeira infância que acompanha serviços e recursos para crianças em todas as regiões administrativas.',
        stack: ['TypeScript', 'PostgreSQL', 'REST'],
        liveUrl: 'https://ppi.pi.gov.br/login',
      },
      {
        slug: 'caseme',
        name: 'CaseMe',
        summary:
          'Planejador de eventos e casamentos com orçamento em tempo real, fluxo de fornecedores e RSVP. Isolamento multi-tenant com Row-Level Security do Supabase, DOMPurify contra XSS e testes com Vitest.',
        stack: ['React 19', 'TypeScript', 'Supabase', 'Vitest'],
      },
      {
        slug: 'gcpreach',
        name: 'GCPreach',
        summary:
          'Copiloto de estudo que transforma anotações manuscritas (OCR), rascunhos e transcrições do YouTube em esboços estruturados com Gemini. Cai do Gemini 2.5 Flash para o 2.0 Flash em HTTP 503 e exige saída em JSON schema.',
        stack: ['Gemini API', 'React 19', 'Supabase', 'shadcn/ui'],
      },
      {
        slug: 'gerenciador-github-ia',
        name: 'GitHub AI Automation',
        summary:
          'Lê o histórico de commits pela API REST do GitHub e usa um LLM para resumir pull requests e escrever atualizações de progresso em linguagem simples para stakeholders.',
        stack: ['Python', 'GitHub API', 'LLM'],
      },
      {
        slug: 'jarvis',
        name: 'JarvisAI',
        summary: 'Assistente de voz em Python com reconhecimento de fala e OpenAI.',
        stack: ['Python', 'OpenAI', 'PyAudio'],
      },
    ],
  },
};
